/**
 * The live session: screen, touch control, keys, device audio. This is where a video that was pushed to
 * the phone is published to TikTok by hand — open the app, pick the clip from the gallery, post.
 *
 * The whole panel is keyed on one device. Every stream object (video, audio, input) is created in an
 * effect and destroyed by its cleanup, so a session can only ever touch its own objects — the old
 * viewer kept them in shared state and a dying stream from the previous phone could wipe the new
 * session's id, which killed input while video kept playing.
 *
 * Layout follows the physical device on purpose: power and volume are bars on the frame's right edge,
 * Android's three keys sit under the screen in Android's own order, and everything that is *ours*
 * (audio, bitrate, diagnostics) lives to the right of the phone. Muscle memory beats a button list.
 */

import { ArrowDown, ArrowUp, Bell, Camera, Hourglass, Keyboard, Power, Sun, TriangleAlert, Volume2, VolumeX } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { holdMinutesLeft, holdState, lostThePhone, ownsClaimOf, type LostReason } from './phoneHold'
import { tabId } from './tabId'

import { ApiError, DeviceBusyError } from '../../api/client'
import type { Device } from '../../api/types'
import { phoneName } from '../devices/phoneName'
import { useT } from '../../i18n'
import { RecordControl } from '../scenarios/RecordControl'
import { FlickTracker, MOVE_THROTTLE_MS, normalise } from '../../input/pointer'
import { InputChannel } from '../../input/channel'
import { useApp } from '../../lib/context'
import { toast } from '../../lib/toast'
import { AudioStream, type AudioStat } from '../../media/audio'
import { CanvasPlaceholder } from '../../media/placeholder'
import { VideoStream, type VideoStats } from '../../media/video'
import { Badge, Card, Pill, Status } from '../../ui/components'

const EXPECTED_STARTUP_S = 5 // measured: dispatch → scrcpy boot → control + SPS ready ≈ 4.7s

/** Physical keys worth forwarding when keyboard capture is on. */
const KEY_MAP: Record<string, string> = {
  Enter: 'KEYCODE_ENTER',
  Backspace: 'KEYCODE_DEL',
  Escape: 'KEYCODE_BACK',
  Tab: 'KEYCODE_TAB',
  ArrowUp: 'KEYCODE_DPAD_UP',
  ArrowDown: 'KEYCODE_DPAD_DOWN',
  ArrowLeft: 'KEYCODE_DPAD_LEFT',
  ArrowRight: 'KEYCODE_DPAD_RIGHT',
}

/** The sentence the service sent, not the status code. A 409 for an unplugged phone carries
 *  "the phone is not attached to the machine — check the cable", which is the whole answer; showing
 *  "HTTP 409" instead makes the tester guess. */
function reasonOf(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.detail as { detail?: { hint?: string; error?: string } } | undefined)?.detail
    if (detail?.hint) return detail.hint
    if (detail?.error) return detail.error
  }
  return e instanceof Error ? e.message : String(e)
}

export function SessionPanel({
  device,
  onClosed,
  onSessionChanged,
}: {
  device: Device
  onClosed: () => void
  onSessionChanged: (sessionId: string | null) => void
}) {
  const { api } = useApp()
  const t = useT()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const videoRef = useRef<VideoStream | null>(null)
  const audioRef = useRef<AudioStream | null>(null)
  const toneOnly = useRef<AudioStream | null>(null) // context for the test tone before audio is on
  const inputRef = useRef<InputChannel | null>(null)
  const flick = useRef(new FlickTracker())
  const holding = useRef(false)
  const lastMove = useRef(0)
  const readyRef = useRef(false)

  const [sessionId, setSessionId] = useState<string | null>(null)
  const [phase, setPhase] = useState<'starting' | 'live' | 'busy' | 'error' | 'elsewhere'>('starting')
  const [message, setMessage] = useState('')
  const [busyInfo, setBusyInfo] = useState<DeviceBusyError | null>(null)
  const [videoStatus, setVideoStatus] = useState('')
  const [stats, setStats] = useState<VideoStats | null>(null)
  const [inputStatus, setInputStatus] = useState('')
  const [inputBad, setInputBad] = useState(false)
  const [ready, setReady] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [attempt, setAttempt] = useState(0)
  //: The deliberate second click on "it is open in another tab": move the picture here.
  const [here, setHere] = useState(false)
  //: Did *this* tab take the claim? A panel that only reattached to somebody else's (another tab, the
  //: person's own Claude over MCP) must not stop it — on close or on "Disconnect" — that would take the
  //: phone from whoever holds it. A reload is the same tab and keeps its right to release.
  const ownsClaim = useRef(true)
  //: When this person last did something the service counts. Not a frame, not a poll.
  const lastAction = useRef(Date.now())
  const [hold, setHold] = useState(holdState(Date.now(), Date.now()))
  const [quality, setQuality] = useState('720x1600:3000000')
  const [audioOn, setAudioOn] = useState(false)
  const [audioStat, setAudioStat] = useState<AudioStat | null>(null)
  const [gain, setGain] = useState(1)
  const [text, setText] = useState('')
  const [capture, setCapture] = useState(false)
  const [closing, setClosing] = useState(false)

  // one session per (device, attempt): switching either tears the old one down completely
  useEffect(() => {
    let cancelled = false
    const canvas = canvasRef.current
    if (!canvas) return

    readyRef.current = false
    setReady(false)
    setStats(null)
    setVideoStatus('')
    setPhase('starting')
    setMessage('')
    setElapsed(0)
    const placeholder = new CanvasPlaceholder(canvas)
    placeholder.start('')

    let stopSession: (() => Promise<void>) | null = null

    void (async () => {
      let started
      try {
        started = await api.sessionStart(device.device_id, { tab: tabId(), here })
      } catch (e) {
        // A cancelled effect used to return here without a word, leaving the screen in "starting"
        // with the timer running — the failure was known and invisible. Cancellation means this
        // panel is being replaced, so log it and stop the placeholder rather than pretend.
        if (cancelled) {
          console.warn('[device-lab] session:start failed for a cancelled panel', e)
          placeholder.stop()
          return
        }
        if (e instanceof DeviceBusyError) {
          setPhase('busy')
          setBusyInfo(e)
          placeholder.stop()
          return
        }
        setPhase('error')
        setMessage(reasonOf(e))
        placeholder.stop()
        return
      }
      if (started.open_elsewhere) {
        //: One person, another tab: nothing was started, and starting a second reader on a phone
        //: with one encoder only makes both pictures stutter.
        setPhase('elsewhere')
        placeholder.stop()
        return
      }
      ownsClaim.current = ownsClaimOf(started)
      lastAction.current = Date.now()
      if (cancelled) {
        if (ownsClaim.current) void api.sessionStop(started.session_id)
        return
      }
      setSessionId(started.session_id)
      onSessionChanged(started.session_id)
      setPhase('live')

      //: The phone is no longer this session's. Automatic posting is the one thing that takes a phone
      //: from a person (2026-10-08) and it deserves its own sentence: a priority task, nothing lost —
      //: not "the session was ended", which reads like a colleague or a crash.
      const lost = (reason: LostReason | null) => {
        setPhase('error')
        setMessage(reason === 'preempted_by_post' ? t.session.preemptedByPost : t.session.endedOnServer)
        videoRef.current?.stop()
        inputRef.current?.close()
      }

      const input = new InputChannel({
        api,
        sessionId: started.session_id,
        t,
        onStatus: (t2, bad) => {
          setInputStatus(t2)
          setInputBad(Boolean(bad))
        },
        //: 1008 says only "not yours any more"; one cheap session-scoped call learns why.
        onRefused: () => {
          void api.sessionKeep(started.session_id).catch((e: unknown) => {
            if (e instanceof ApiError) lost(lostThePhone(e.status, e.detail))
          })
        },
      })
      input.open()
      inputRef.current = input

      // The phone's screen is usually off, so the stream would start black. Wake it, dismiss the
      // lockscreen, and keep nudging it awake for as long as the session lives.
      //: `auto`: the panel's own nudges are not a person — counted as activity they would renew the
      //: claim for ever from an empty room.
      input.send('key', { key: 'KEYCODE_WAKEUP', auto: true })
      const dismiss = setTimeout(
        () => input.send('swipe', { nx1: 0.5, ny1: 0.8, nx2: 0.5, ny2: 0.25, duration_ms: 200, auto: true }),
        350,
      )
      const keepAwake = setInterval(() => input.send('key', { key: 'KEYCODE_WAKEUP', auto: true }), 15000)

      const video = new VideoStream({
        api,
        sessionId: started.session_id,
        canvas,
        onStatus: setVideoStatus,
        onStats: setStats,
        onFirstFrame: () => {
          placeholder.stop()
          readyRef.current = true
          setReady(true)
        },
        onSessionEnded: lost,
      })
      video.start()
      videoRef.current = video

      stopSession = async () => {
        clearTimeout(dismiss)
        clearInterval(keepAwake)
        video.stop()
        input.close()
        audioRef.current?.stop()
        audioRef.current = null
        if (ownsClaim.current) await api.sessionStop(started.session_id)
      }
    })()

    return () => {
      cancelled = true
      placeholder.stop()
      videoRef.current?.stop()
      videoRef.current = null
      inputRef.current?.close()
      inputRef.current = null
      audioRef.current?.stop()
      audioRef.current = null
      toneOnly.current?.stop()
      toneOnly.current = null
      setAudioOn(false)
      setAudioStat(null)
      void stopSession?.()
      onSessionChanged(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.device_id, attempt])

  // The idle warning: the phone frees itself after ten minutes of nothing a person did.
  useEffect(() => {
    if (phase !== 'live') return
    const id = setInterval(() => setHold(holdState(lastAction.current, Date.now())), 1000)
    return () => clearInterval(id)
  }, [phase])

  // Startup takes ~5s and the tester deserves to see it moving rather than a silent placeholder.
  useEffect(() => {
    if (ready) return
    const id = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [ready])

  // A closed tab must not hold a phone for the whole idle TTL.
  useEffect(() => {
    const release = () => {
      if (sessionId && ownsClaim.current) void api.sessionStop(sessionId)
    }
    window.addEventListener('pagehide', release)
    return () => window.removeEventListener('pagehide', release)
  }, [api, sessionId])

  const send = useCallback((action: Parameters<InputChannel['send']>[0], params?: Record<string, unknown>) => {
    if (!params?.auto) lastAction.current = Date.now()
    inputRef.current?.send(action, params)
  }, [])

  const keep = async () => {
    if (!sessionId) return
    try {
      await api.sessionKeep(sessionId)
      lastAction.current = Date.now()
      setHold(holdState(lastAction.current, Date.now()))
    } catch (e) {
      setPhase('error')
      const reason = e instanceof ApiError ? lostThePhone(e.status, e.detail) : null
      setMessage(reason === 'preempted_by_post' ? t.session.preemptedByPost : reasonOf(e))
    }
  }

  const key = (code: string) => send('key', { key: code })

  // Keyboard capture: type straight into the phone instead of the text box. Off by default, because
  // swallowing every keystroke on a page with its own inputs would be hostile.
  useEffect(() => {
    if (!capture || !ready) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      const mapped = KEY_MAP[e.key]
      if (mapped) {
        e.preventDefault()
        send('key', { key: mapped })
        return
      }
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault()
        send('text', { text: e.key })
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [capture, ready, send])

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    return normalise(e.clientX, e.clientY, rect)
  }

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    if (!readyRef.current) {
      setInputStatus(t.session.notReady)
      setInputBad(true)
      return
    }
    e.currentTarget.setPointerCapture?.(e.pointerId)
    const p = point(e)
    holding.current = true
    flick.current.reset()
    flick.current.sample(p, performance.now())
    send('touch_down', p)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!holding.current) return
    const p = point(e)
    flick.current.sample(p, performance.now()) // every event: velocity stays honest
    const now = Date.now()
    if (now - lastMove.current < MOVE_THROTTLE_MS) return
    lastMove.current = now
    send('touch_move', p)
  }

  const release = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!holding.current) return
    holding.current = false
    const p = point(e)
    flick.current.sample(p, performance.now())
    send('touch_up', { ...p, ...flick.current.velocity() })
  }

  const toggleAudio = async () => {
    if (audioRef.current) {
      audioRef.current.stop()
      audioRef.current = null
      setAudioOn(false)
      setAudioStat(null)
      return
    }
    if (!sessionId) return
    const stream = new AudioStream({
      api,
      sessionId,
      gain,
      t,
      onStat: setAudioStat,
      onUnavailable: (hint) => {
        // Show the server's own reason. Never translate "no audio" into a guess about the phone.
        toast(hint, 'bad')
        setAudioStat(null)
        audioRef.current = null
        setAudioOn(false)
      },
    })
    audioRef.current = stream
    setAudioOn(true)
    await stream.start()
  }

  const disconnect = async () => {
    setClosing(true)
    videoRef.current?.stop()
    audioRef.current?.stop()
    audioRef.current = null
    inputRef.current?.close()
    if (sessionId && ownsClaim.current) {
      try {
        await api.sessionStop(sessionId)
      } catch {
        /* the panel is closing either way — a failed stop is cleaned up by the idle timeout */
      }
    }
    onClosed()
  }

  const screenshot = async () => {
    if (!sessionId) return
    const res = await api.screenshot(sessionId)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${device.serial}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`
    a.click()
    URL.revokeObjectURL(url)
    toast(t.session.screenshotSaved)
  }

  const progress = Math.min(95, Math.round((elapsed / EXPECTED_STARTUP_S) * 100))
  const QUALITY = [
    { value: '720x1600:3000000', label: t.session.q3 },
    { value: '720x1600:5000000', label: t.session.q5 },
    { value: '720x1600:1500000', label: t.session.q15 },
  ]

  const minutesLeft = holdMinutesLeft(lastAction.current, Date.now())

  return (
    <Card className="session-card">
      {/* **The session bar** (redesign stage 2, from Device Lab): which phone, on which machine, with
          which TikTok account, live or not, how long it stays yours, and the way out — one line above
          the phone instead of a card title and a status hidden at its far edge. */}
      <div className="session-bar">
        <span className={`session-dot ${closing || !ready ? 'warn' : 'ok'}`} aria-hidden />
        <div className="session-who">
          <span className="session-name">
            <strong>{phoneName(device)}</strong>
            {closing ? (
              <Badge kind="warn">{t.session.disconnecting}</Badge>
            ) : ready ? (
              <Badge kind="ok">{t.session.live}</Badge>
            ) : (
              <Badge kind="warn">{t.session.starting}</Badge>
            )}
          </span>
          <span className="session-meta">
            {device.hub_id ? <span className="on-machine">{device.hub_id}</span> : null}
            {device.tiktok_account ? <span className="mono">@{device.tiktok_account.replace(/^@/, '')}</span> : null}
            {device.os_version ? <span>Android {device.os_version}</span> : null}
            <span className="mono">{device.serial}</span>
          </span>
        </div>
        <span className="grow" />
        <div className="session-state">
          {sessionId && phase === 'live' && hold.state === 'held' && (
            <span className="pill" title={t.session.holdLeftTitle}>
              <Hourglass className="ico" size={13} aria-hidden /> {t.session.holdLeft(minutesLeft)}
            </span>
          )}
          <button className="ghost" disabled={closing} onClick={() => void disconnect()}>
            <Power className="ico" size={15} aria-hidden />
            {closing ? t.session.disconnecting : t.session.disconnect}
          </button>
        </div>
      </div>
      {phase === 'busy' && (
        <div className="banner" style={{ marginBottom: 'var(--sp-3)' }}>
          <span>{busyInfo?.kind === 'device_posting' ? '🤖' : '👥'}</span>
          <span className="grow">
            {busyInfo?.kind === 'device_posting'
              ? t.session.postingBody(busyInfo.heldBy)
              : t.session.busyBody(
                  busyInfo?.heldBy ?? null,
                  busyInfo?.heldForS != null ? Math.round(busyInfo.heldForS / 60) : null,
                )}
          </span>
          <button className="ghost" onClick={onClosed}>
            {t.session.close}
          </button>
        </div>
      )}
      {phase === 'elsewhere' && (
        <div className="banner" style={{ marginBottom: 'var(--sp-3)' }}>
          <span className="grow">{t.session.openElsewhere}</span>
          <button className="primary" onClick={() => {
            setHere(true)
            setAttempt((n) => n + 1)
          }}>
            {t.session.showHere}
          </button>
          <button className="ghost" onClick={onClosed}>{t.session.close}</button>
        </div>
      )}
      {phase === 'live' && hold.state !== 'held' && (
        <div className="banner warn" style={{ marginBottom: 'var(--sp-3)' }}>
          <span className="grow">
            {hold.state === 'warning' ? t.session.holdWarning(hold.inS) : t.session.lapsed}
          </span>
          <button className="primary" onClick={() => void keep()}>{t.session.keep}</button>
        </div>
      )}
      {phase === 'error' && (
        <div className="banner bad" style={{ marginBottom: 'var(--sp-3)' }}>
          <span><TriangleAlert className="ico" size={16} aria-hidden /></span>
          <span className="grow">{message || t.session.failed}</span>
          <button onClick={() => setAttempt((n) => n + 1)}>{t.session.retry}</button>
          <button className="ghost" onClick={onClosed}>
            {t.session.close}
          </button>
        </div>
      )}

      <div className="stage">
        {/* Phone and rail stay on one line — under a tall phone is below the fold. */}
        <div className="phone-and-rail">
        <div className="phone-wrap">
          <div className="phone">
            <div className="screen-wrap">
              <canvas
                ref={canvasRef}
                className={`screen${ready ? '' : ' waiting'}`}
                width={360}
                height={800}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={release}
                onPointerCancel={release}
                onPointerLeave={release}
              />
              {closing && (
                <div className="startup">
                  <strong>{t.session.disconnecting}</strong>
                </div>
              )}
              {/* Not while busy, and **not on error**: the overlay used to keep counting seconds and
                  saying "the hub is bringing the stream up" over a session that had already failed —
                  which is what "I press take over, a timer runs, nothing else happens" was. The error
                  banner above says what went wrong; the screen must not contradict it. */}
              {!ready && !closing && phase !== 'busy' && phase !== 'error' && (
                <div className="startup">
                  <strong>{videoStatus || t.session.hubStarting}</strong>
                  <div className="bar">
                    <i style={{ width: `${progress}%` }} />
                  </div>
                  <span className="tiny dim">{t.session.startupNote(elapsed, EXPECTED_STARTUP_S)}</span>
                </div>
              )}
            </div>

            {/* wake on the left, power and volume on the right — where the fingers reach on a real phone */}
            <div className="hard-keys left">
              <button className="hard" title={t.session.wake} aria-label={t.session.wake} onClick={() => key('KEYCODE_WAKEUP')}>
                <span><Sun className="ico" size={16} aria-hidden /></span>
              </button>
            </div>
            <div className="hard-keys right">
              <button className="hard" title={t.session.power} aria-label={t.session.power} onClick={() => key('KEYCODE_POWER')}>
                <span><Power className="ico" size={15} aria-hidden /></span>
              </button>
              <button className="hard" title={t.session.volUp} aria-label={t.session.volUp} onClick={() => key('KEYCODE_VOLUME_UP')}>
                <span>＋</span>
              </button>
              <button className="hard" title={t.session.volDown} aria-label={t.session.volDown} onClick={() => key('KEYCODE_VOLUME_DOWN')}>
                <span>−</span>
              </button>
            </div>
          </div>

          {/* Android's own three keys, in Android's own order, under the screen */}
          <div className="navbar">
            <button title={t.session.back} aria-label={t.session.back} onClick={() => key('KEYCODE_BACK')}>
              ◁
            </button>
            <button title={t.session.home} aria-label={t.session.home} onClick={() => key('KEYCODE_HOME')}>
              ○
            </button>
            <button title={t.session.recents} aria-label={t.session.recents} onClick={() => key('KEYCODE_APP_SWITCH')}>
              □
            </button>
          </div>
        </div>

        {/* **Actions next to the phone** (redesign stage 2): what is done *to* the phone, icon over
            label, against its frame; the side panel keeps what is typed or tuned. On a narrow stage it
            becomes one row of icons above the phone. */}
        <div className="phone-rail" role="toolbar" aria-label={t.session.actions}>
          <button className="rail-btn" disabled={!sessionId} title={t.session.screenshot}
                  aria-label={t.session.screenshot} onClick={() => void screenshot()}>
            <Camera className="ico" size={18} aria-hidden /><span>{t.session.screenshot}</span>
          </button>
          <RecordControl sessionId={sessionId} deviceId={device.device_id} rail />
          <span className="rail-sep" aria-hidden />
          <button className={`rail-btn${capture ? ' primary' : ''}`} aria-pressed={capture}
                  title={t.session.keyboardTitle} aria-label={t.session.keyboard}
                  onClick={() => setCapture((c) => !c)}>
            <Keyboard className="ico" size={18} aria-hidden />
            <span>{capture ? t.session.keyboardOn : t.session.keyboard}</span>
          </button>
          <button className="rail-btn" disabled={!sessionId} title={t.session.swipeUp} aria-label={t.session.swipeUp}
                  onClick={() => send('swipe', { nx1: 0.5, ny1: 0.75, nx2: 0.5, ny2: 0.25, duration_ms: 220 })}>
            <ArrowUp className="ico" size={18} aria-hidden /><span>{t.session.swipeUp}</span>
          </button>
          <button className="rail-btn" disabled={!sessionId} title={t.session.swipeDown} aria-label={t.session.swipeDown}
                  onClick={() => send('swipe', { nx1: 0.5, ny1: 0.25, nx2: 0.5, ny2: 0.75, duration_ms: 220 })}>
            <ArrowDown className="ico" size={18} aria-hidden /><span>{t.session.swipeDown}</span>
          </button>
        </div>
        </div>

        <div className="side">
          <div className="row">
            <input
              className="grow"
              placeholder={t.session.typeHere}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && text) {
                  send('text', { text })
                  setText('')
                }
              }}
            />
            <button
              disabled={!text}
              onClick={() => {
                send('text', { text })
                setText('')
              }}
            >
              {t.session.send}
            </button>
          </div>
          <div className="row">
            <span className="tiny dim">{t.session.gestureHint}</span>
          </div>
          {capture && <span className="tiny dim">{t.session.keyboardHint}</span>}
          <div className="row">
            <Status text={inputStatus} bad={inputBad} />
          </div>
          {/* Sound, quality and the live numbers last: tuned once, read when something looks wrong. */}
          <div className="panel">
          <div className="panel-row">
            <button onClick={() => void toggleAudio()} title={t.audio.toggleTitle}>
              {audioOn ? <><Volume2 className="ico" size={16} aria-hidden /> {t.audio.on}</> : <><VolumeX className="ico" size={16} aria-hidden /> {t.audio.off}</>}
            </button>
            <input
              type="range"
              min={0}
              max={200}
              value={gain * 100}
              style={{ width: 90 }}
              aria-label={t.audio.gain}
              title={t.audio.gain}
              onChange={(e) => {
                const v = Number(e.target.value) / 100
                setGain(v)
                audioRef.current?.setGain(v)
              }}
            />
            <button
              className="ghost"
              title={t.audio.toneTitle}
              onClick={async () => {
                const stream =
                  audioRef.current ?? toneOnly.current ?? new AudioStream({ api, sessionId: sessionId ?? '', gain, t })
                if (!audioRef.current) toneOnly.current = stream
                setInputStatus(await stream.testTone())
                setInputBad(false)
              }}
            >
              <Bell className="ico" size={15} aria-hidden /> {t.audio.tone}
            </button>
            <span className="sep" />
            <select
              value={quality}
              aria-label={t.session.quality}
              title={t.session.qualityTitle}
              onChange={(e) => {
                setQuality(e.target.value)
                videoRef.current?.setQuality(e.target.value)
              }}
            >
              {QUALITY.map((q) => (
                <option key={q.value} value={q.value}>
                  {q.label}
                </option>
              ))}
            </select>
          </div>

          {/* Telemetry lives in the same panel as the controls it explains: link speed, video and
              audio in real time. Watching them is how a stalled stream, a starved channel and a
              quiet phone are told apart — scattered across the page, nobody watches them at all. */}
          <div className="panel-row telemetry">
            <Pill mono title={t.session.video}>
              {t.session.video}:{' '}
              {stats
                ? `${stats.fps} fps · ${stats.kbit} kbit · q${stats.queue} · ${stats.decodeMs} ms`
                : videoStatus || t.session.hubStarting}
            </Pill>
            {audioStat ? (
              <Pill mono title={t.audio.title}>
                {t.audio.title}: {audioStat.wire} · {t.audio.decoded(audioStat.decodedPerSec)} ·{' '}
                {t.audio.ctx(audioStat.ctxState)} · {t.audio.gainValue(gain.toFixed(2))}
                {audioStat.cfg ? ` · ${audioStat.cfg}` : ''}
              </Pill>
            ) : null}
            {audioStat?.error ? <Badge kind="bad">{audioStat.error}</Badge> : null}
            {audioStat && audioStat.decodedPerSec > 0 && audioStat.ctxState === 'running' && !audioStat.error ? (
              <span className="tiny dim">{t.audio.fineHere}</span>
            ) : null}
            {sessionId ? (
              <Pill mono title={t.session.sessionId}>
                {sessionId.slice(0, 8)}
              </Pill>
            ) : null}
          </div>
          </div>

        </div>
      </div>
    </Card>
  )
}
